import { useMemo, useRef, useState, useEffect } from 'react';
import { IconAnchor, IconBell, IconChartBar, IconChecklist, IconMail, IconClock, IconDeviceFloppy, IconDots, IconLayoutColumns, IconPlus, IconSearch, IconShip, IconCalendar, IconCalendarEvent, IconFilter } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { useNavigate } from '@tanstack/react-router';
import { useT, t as tr } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Chip, Input, Segmented, SelectionBar, StateView, Skeleton, useToast, toggleAll, toggleOne, type ScreenState, type Tone } from '@hana/ui';
import type { Vessel } from '../pricing/types';
import { VesselDrawer } from '../pricing/VesselDrawer';
import { JOB_BOARD_NOW } from './fixtures';
import { carrierName, cutoffLabel, dayLabel, initials, portLabel } from './format';
import { JOB_TABS, JOB_TABS_MORE, PROGRESS_STEPS, isUrgent, jobGroup, jobProgress, nearCutoff, queryJobs, tabCounts, type JobSortKey, type JobTab } from './jobs';
import type { Job } from './types';
import { GROUP_DOT, GridCard, PhoneCard } from './JobCards';
import './jobListI18n';
import { useLayout } from './useLayout';
import { JobDock, type DockTab } from './JobDock';
import './jobDockI18n';
import { usePageScroll } from './usePageScroll';
import { virtualWindow } from './virtual';
import s from './jobs.module.css';

// Mint-Job (desktop 1536): tab trạng thái · tìm · bảng 9 cột mặc định, cuộn ảo. Chỉ đọc; nút ghi báo toast.
// "Bây giờ" lấy mốc của dữ liệu mẫu (22/Oct 10:30); đổi sang giờ thật cùng lúc chuyển dữ liệu thật (D-06).

const ROW_H = 95;
const CARD_H = 194; // thẻ điện thoại 184 + khe 10
const GRID_H = 142; // một hàng 3 thẻ iPad: 130 + khe 12
const GRID_COLS = 3;
const NOW = JOB_BOARD_NOW;

const SCOPES = ['Container', 'JobID', 'Booking', 'B/L', 'Khách hàng', 'Tàu'] as const;
const VIEWS = ['Bảng', 'Theo tàu', 'Thẻ', 'Thống kê'] as const;


const FLAGS: Record<string, { label: string; tip: string; tone: Tone }> = {
  noq: { label: 'Chưa có báo giá', tip: 'Chưa gắn báo giá', tone: 'warn' },
  qdiff: { label: 'Lệch báo giá', tip: 'Giá lệch báo giá đã chấp nhận', tone: 'warn' },
  dg: { label: 'Hàng DG', tip: 'Hàng nguy hiểm', tone: 'err' },
  task: { label: 'Việc quá hạn', tip: 'Có task quá hạn', tone: 'err' },
};

interface ColDef {
  key: string;
  header: string;
  width: number;
  sort?: JobSortKey;
  minor?: boolean;
  /** Board Mint-Job (compact = 1280): Other Party ẩn mặc định dưới 1440. */
  cmp?: boolean;
  cell: (j: Job) => React.ReactNode;
}

const Sub = ({ children, icon, tip, hot }: { children: React.ReactNode; icon?: React.ReactNode; tip?: string; hot?: boolean }) =>
  children ? (
    <div className={`${s.sub} ${hot ? s.hot : ''}`} title={tip}>
      {icon}
      <span>{children}</span>
    </div>
  ) : null;

function jobFlags(j: Job): { key: string; label: string; tip: string; tone: Tone }[] {
  const out = j.flags.filter((f) => FLAGS[f]).map((f) => ({ key: f, ...(FLAGS[f] as (typeof FLAGS)[string]) }));
  if (j.status === 'No Show') out.push({ key: 'noshow', label: 'No Show', tip: 'No Show · giữ job thu phí', tone: 'warn' });
  if (j.flags.includes('follow')) out.push({ key: 'follow', label: 'Follow', tip: 'Đang follow', tone: 'info' });
  return out;
}

const vesselKey = (j: Job) => (j.vessel ? j.vessel.replace(/\s(\S+)$/, '|$1') : '');

/** Chưa có hồ sơ tàu trong dữ liệu `vessel`: dựng từ Job (chưa có IMO). */
function vesselFromJob(j: Job): Vessel {
  const [name = '', voyage = ''] = vesselKey(j).split('|');
  return { id: vesselKey(j), name, voyage, carrier: j.carrier ?? '', service: null, terminal: '', pol: j.pol, noticeType: '', etd: j.etd ?? '', cy: j.cyCutoff ?? '', icd: '', si: '', atd: '', remark: '', changes: j.delayCount, profile: null };
}

function buildColumns(onOpen: (j: Job) => void, onMore: (j: Job) => void, onVessel: (j: Job) => void): ColDef[] {
  return [
    {
      key: 'no',
      header: 'JobID',
      width: 164,
      sort: 'id',
      cell: (j) => {
        const g = jobGroup(j.status, j.close);
        const flags = jobFlags(j);
        return (
          <>
            <button type="button" className={s.jobNo} onClick={() => onOpen(j)}>
              {j.id}
            </button>
            <div className={s.stRow}>
              <button type="button" className={s.stBtn} aria-label={`Đổi trạng thái ${j.id}`} title="ShipmentStatus" onClick={() => onMore(j)}>
                <Chip dot={GROUP_DOT[g]}>
                  {j.status} <span aria-hidden="true">▾</span>
                </Chip>
              </button>
              {j.close && <span className={`${s.close} ${j.close === 'Quá hạn đóng' ? s.hot : ''}`}>{j.close}</span>}
            </div>
            {flags[0] && (
              <div className={s.flags}>
                <Chip tone={flags[0].tone} label={flags[0].tip}>
                  {flags[0].label}
                </Chip>
                {flags.length > 1 && (
                  <span className={s.more} title={flags.slice(1).map((f) => f.tip).join(' · ')}>
                    +{flags.length - 1}
                  </span>
                )}
              </div>
            )}
          </>
        );
      },
    },
    {
      key: 'cus',
      header: 'Khách hàng',
      width: 140,
      sort: 'customer',
      cell: (j) => (
        <>
          <div className={s.main}>{j.customer}</div>
          <Sub>{j.consignee}</Sub>
        </>
      ),
    },
    {
      key: 'shp',
      header: 'Shipment',
      width: 160,
      sort: 'shipment',
      cell: (j) => (
        <>
          <div className={`${s.main} ${j.bookingNo ? '' : s.muted}`}>{j.bookingNo ?? (jobGroup(j.status, j.close) === 'Standby' ? 'Chưa có booking' : '—')}</div>
          <Sub>{j.extraRefs[0]}</Sub>
          {j.services.length > 0 && (
            <div className={s.chips}>
              {j.services.slice(0, 2).map((v) => (
                <span key={v} className={s.svc}>
                  {v}
                </span>
              ))}
              {j.services.length > 2 && <span className={s.more}>+{j.services.length - 2}</span>}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'cargo',
      header: 'Cargo',
      width: 140,
      sort: 'cargo',
      minor: true,
      cell: (j) => (
        <>
          <div className={s.main}>{j.qty.replace('×', ' × ')}</div>
          <Sub>{j.commodity}</Sub>
        </>
      ),
    },
    {
      key: 'from',
      header: 'From',
      width: 170,
      sort: 'cut',
      cell: (j) => {
        const cy = cutoffLabel(j.cyCutoff);
        const hot = nearCutoff(j, NOW);
        return (
          <>
            <div className={s.main}>{portLabel(j.pol)}</div>
            <Sub icon={cy ? <IconClock size={12} aria-hidden="true" /> : undefined} tip={cy ? `CY cut-off ${cy}` : undefined} hot={hot}>
              {cy}
            </Sub>
          </>
        );
      },
    },
    {
      key: 'tr',
      header: 'Transportation',
      width: 190,
      sort: 'etd',
      cell: (j) => {
        const etd = dayLabel(j.etd);
        const car = carrierName(j.carrier);
        return (
          <>
            {j.vessel ? (
              <span data-vlink={vesselKey(j)} tabIndex={0} className={`${s.main} ${s.vlink}`} title={tr('jobList.openVessel')} onClick={() => onVessel(j)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onVessel(j); } }}>{j.vessel}</span>
            ) : (
              <div className={`${s.main} ${j.vessel ? '' : s.muted}`}>{j.vessel ?? 'Chưa có tàu'}</div>
            )}
            <Sub icon={<IconShip size={12} aria-hidden="true" />} tip={car ? `Hãng ${car}${etd ? ` · ETD ${etd}` : ''}` : undefined}>
              {[car, etd].filter(Boolean).join(' · ')}
            </Sub>
            {j.delayCount > 0 && (
              <div className={s.chips}>
                <Chip tone="warn" label="Dời lịch">
                  +{j.delayCount} ngày
                </Chip>
              </div>
            )}
          </>
        );
      },
    },
    {
      key: 'to',
      header: 'To',
      width: 150,
      sort: 'pod',
      cell: (j) => {
        const eta = dayLabel(j.eta);
        return (
          <>
            <div className={s.main}>{portLabel(j.pod)}</div>
            <Sub icon={eta ? <IconCalendarEvent size={12} aria-hidden="true" /> : undefined} tip={eta ? `ETA ${eta}` : undefined}>
              {eta}
            </Sub>
          </>
        );
      },
    },
    {
      key: 'oth',
      header: 'Other Party',
      width: 190,
      minor: true,
      cmp: true,
      cell: (j) => (
        <>
          <div className={`${s.main} ${j.shipper ? '' : s.muted}`}>{j.shipper || '—'}</div>
          <Sub>{j.consignee}</Sub>
        </>
      ),
    },
    {
      key: 'act',
      header: '',
      width: 64,
      cell: (j) => (
        <div className={s.act}>
          <button type="button" className={s.dots} aria-label={`${tr('jobList.actions')} ${j.id}`} title={tr('jobList.actions')} onClick={() => onMore(j)}>
            <IconDots size={16} aria-hidden="true" />
          </button>
          <span className={s.pic} title={`Sale ${j.sale} · CS ${j.cs}${j.bkg ? ` · BKG ${j.bkg}` : ''}`} aria-label={`Sale ${j.sale}`}>
            {initials(j.sale)}
          </span>
          <div className={s.prog} role="img" aria-label={`Tiến độ ${jobProgress(j)}/${PROGRESS_STEPS}`} title={`Tiến độ ${jobProgress(j)}/${PROGRESS_STEPS}`}>
            <div style={{ width: `${Math.round((jobProgress(j) / PROGRESS_STEPS) * 100)}%` }} />
          </div>
        </div>
      ),
    },
  ];
}

/** Bảng iPad (Mint-Job-iPad): JobID · Khách hàng · Shipment · Transportation · From → To · PIC · tiến độ. */
function padColumns(base: ColDef[]): ColDef[] {
  const by = (k: string) => base.find((c) => c.key === k) as ColDef;
  const cus = by('cus');
  const shp = by('shp');
  return [
    { ...by('no'), sort: undefined },
    {
      key: 'cus',
      header: 'Khách hàng · Shipment',
      width: 170,
      cell: (j) => (
        <>
          {cus.cell(j)}
          {shp.cell(j)}
        </>
      ),
    },
    { ...by('tr'), width: 240, sort: undefined },
    {
      key: 'route',
      header: 'From → To',
      width: 330,
      cell: (j) => {
        const cy = cutoffLabel(j.cyCutoff);
        const eta = dayLabel(j.eta);
        return (
          <>
            <div className={s.main}>
              {portLabel(j.pol)} → {portLabel(j.pod)}
            </div>
            <div className={s.routeSub}>
              <Sub icon={cy ? <IconClock size={12} aria-hidden="true" /> : undefined} tip={cy ? `CY cut-off ${cy}` : undefined} hot={nearCutoff(j, NOW)}>
                {cy}
              </Sub>
              <Sub icon={eta ? <IconCalendarEvent size={12} aria-hidden="true" /> : undefined} tip={eta ? `ETA ${eta}` : undefined}>
                {eta}
              </Sub>
            </div>
          </>
        );
      },
    },
    { ...by('act'), header: 'PIC · tiến độ' },
  ];
}

function SkeletonTable() {
  return <Skeleton rows={9} />;
}

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

export function JobList() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { data, error, isLoading, isError, refetch } = useList('job', { limit: 5000 });
  const forced = readState();
  const vessels = useList('vessel');
  const [vesselOf, setVesselOf] = useState<Job | null>(null);
  const profiles = useMemo(() => new Map(((vessels.data?.items ?? []) as Vessel[]).map((v) => [v.id, v])), [vessels.data]);
  // Job lưu "TÊN CHUYẾN" (YM CERTAINTY 089N); hồ sơ tàu khoá "TÊN|CHUYẾN". Chỉ mở khi đã có hồ sơ.
  const openVessel = vesselOf ? (profiles.get(vesselKey(vesselOf)) ?? vesselFromJob(vesselOf)) : null;
  const [tab, setTab] = useState<JobTab>('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<{ key: JobSortKey; dir: 'asc' | 'desc' } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const layout = useLayout();
  const [urgent, setUrgent] = useState(false);
  const [qMode, setQMode] = useState<string[]>([]);
  const [qDir, setQDir] = useState<string[]>([]);
  const [view, setViewMode] = useState<'table' | 'card'>('table');
  const [searchOn, setSearchOn] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [scope, setScope] = useState<string>('Container');
  const [dockOn, setDockOn] = useState(true);
  const [dockTab, setDockTab] = useState<DockTab>('an');
  const [wide, setWide] = useState(() => matchMedia('(min-width: 1800px)').matches);
  useEffect(() => {
    const m = matchMedia('(min-width: 1800px)');
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  const asCards = layout === 'phone';
  const asGrid = layout === 'ipad' && view === 'card';

  const all = data?.items ?? [];
  const counts = useMemo(() => tabCounts(all, NOW), [all]);
  const rows = useMemo(() => {
    const out = queryJobs(all, { tab, q, now: NOW, sort: sort ?? undefined, mode: qMode as Job['mode'][] });
    const byDir = qDir.length ? out.filter((j) => (qDir.includes('OB') && j.dir === 'OB') || (qDir.includes('IB') && j.dir !== 'OB')) : out;
    return urgent ? byDir.filter((j) => isUrgent(j, NOW)) : byDir;
  }, [all, tab, q, sort, urgent, qMode, qDir]);

  const noWrite = () => toast({ msg: t('jobList.noWrite') });
  const open = (j: Job) => void navigate({ to: `/ezy/jobs/${j.id}` });
  const newJob = () => void navigate({ to: '/ezy/jobs/new' });
  const baseColumns = useMemo(() => buildColumns(open, () => noWrite(), (j) => setVesselOf(j)), []); // eslint-disable-line react-hooks/exhaustive-deps
  const columns = useMemo(() => (layout === 'ipad' ? padColumns(baseColumns) : baseColumns), [baseColumns, layout]);

  const scroller = useRef<HTMLDivElement>(null);
  const scrollView = usePageScroll(scroller, [rows.length, asCards, asGrid, layout]);
  useEffect(() => setSelected(new Set()), [tab, q]);
  const win = asGrid
    ? virtualWindow(Math.ceil(rows.length / GRID_COLS), GRID_H, scrollView.top, scrollView.h)
    : virtualWindow(rows.length, asCards ? CARD_H : ROW_H, scrollView.top, scrollView.h);
  const keys = rows.map((r) => r.id);
  const bodyW = columns.filter((c) => c.key !== 'act').reduce((a, c) => a + c.width, 0);

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (all.length === 0) state = 'empty';

  const sortBy = (key: JobSortKey) => setSort((c) => ({ key, dir: c?.key === key && c.dir === 'desc' ? 'asc' : 'desc' }));
  const tabsReady = state === 'ready';
  const showDock = layout === 'desktop' && wide && dockOn && state === 'ready';
  const openDock = (k: DockTab) => {
    if (showDock && dockTab === k) setDockOn(false);
    else {
      setDockTab(k);
      setDockOn(true);
    }
  };

  const moreOn = JOB_TABS_MORE.some((m) => m.key === tab);
  const mainTabs = JOB_TABS.map((t) => ({ key: t.key, label: layout === 'ipad' && t.key === 'sby' ? 'BK Standby' : t.label, n: tabsReady ? counts[t.key] : undefined }));
  const tabsEl =
    layout !== 'phone' ? (
      <div className={s.tabsRow}>
        <Segmented label="Trạng thái job" value={moreOn ? ('' as JobTab) : tab} onChange={setTab} items={mainTabs} />
        <div className={s.moreWrap}>
          <button type="button" className={`${s.moreBtn} ${moreOn ? s.moreOn : ''}`} aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen((v) => !v)}>
            {moreOn ? JOB_TABS_MORE.find((m) => m.key === tab)?.label : 'Thêm'}
            <span aria-hidden="true">▾</span>
          </button>
          {moreOpen && (
            <div className={s.pop} role="menu" aria-label={t('jobList.moreTabs')}>
              {JOB_TABS_MORE.map((m) => (
                <button key={m.key} type="button" role="menuitem" className={s.popItem} onClick={() => { setTab(m.key); setMoreOpen(false); }}>
                  {m.label}
                  {tabsReady && <span className={s.popN}>{counts[m.key]}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    ) : (
      <Segmented
        label="Trạng thái job"
        value={tab}
        onChange={setTab}
        items={[...mainTabs, ...JOB_TABS_MORE.map((t) => ({ key: t.key, label: t.label, n: tabsReady ? counts[t.key] : undefined }))]}
      />
    );
  // Điện thoại (Mint-Job-Phone): hàng chip Của tôi · Gấp · Xem · sắp xếp ▾ · Lọc; các tab trạng thái nằm trong menu Xem.
  const allViews = [...mainTabs, ...JOB_TABS_MORE.map((m) => ({ key: m.key, label: m.label, n: tabsReady ? counts[m.key] : undefined }))];
  const phoneBar = (
    <div className={s.phoneBar}>
      <button type="button" className={`${s.quick} ${tab === 'mine' ? s.quickOn : ''}`} aria-pressed={tab === 'mine'} onClick={() => setTab(tab === 'mine' ? 'all' : 'mine')}>
        {t('jobList.mine')}
      </button>
      <button type="button" className={`${s.quick} ${urgent ? s.quickOn : ''}`} aria-pressed={urgent} title={t('jobList.urgentTip')} onClick={() => setUrgent((v) => !v)}>
        {t('jobList.urgent')}
      </button>
      <div className={s.moreWrap}>
        <button type="button" className={s.quick} aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen((v) => !v)}>
          {t('jobList.viewSort')}
        </button>
        {moreOpen && (
          <div className={s.pop} role="menu" aria-label={t('jobList.viewSort')}>
            {allViews.map((m) => (
              <button key={m.key} type="button" role="menuitem" className={s.popItem} onClick={() => { setTab(m.key); setMoreOpen(false); }}>
                {m.label}
                {m.n !== undefined && <span className={s.popN}>{m.n}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <button type="button" className={`${s.quick} ${s.phoneFilter}`} onClick={noWrite}>
        <IconFilter size={16} aria-hidden="true" />
        {t('jobList.filter')}
      </button>
    </div>
  );
  const nCut = rows.filter((j) => nearCutoff(j, NOW)).length;
  const nTask = rows.filter((j) => j.flags.includes('task')).length;
  const toolbar = (
    <>
      <div className={s.tb}>
        <label className={s.tbSearch}>
          <IconSearch size={16} aria-hidden="true" />
          <select className={s.scope} aria-label={t('jobList.searchScope')} value={scope} onChange={(e) => setScope(e.target.value)}>
            {SCOPES.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
          <input aria-label={t('jobList.searchJob')} placeholder={t('jobList.searchPaste')} value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <button type="button" className={s.tbBtn} onClick={() => toast({ msg: t('jobList.noRange') })}>
          <IconCalendar size={16} aria-hidden="true" />
          {t('jobList.thisMonth')} · 01–31/Oct/2026 <span aria-hidden="true">▾</span>
        </button>
        <button type="button" className={`${s.tbBtn} ${s.tbCtl}`} onClick={() => toast({ msg: t('jobList.noAdv') })}>
          <IconPlus size={16} aria-hidden="true" />
          {t('jobList.filter')}
        </button>
      </div>
      <div className={s.sumRow}>
        <div className={s.sum}>
          <span>
            Job <b>{rows.length}</b>
          </span>
          <span>
            Cut-off &lt; 48h <b className={nCut ? s.sumHot : undefined}>{nCut}</b>
          </span>
          <span>
            {t('jobList.overdue')} <b className={nTask ? s.sumHot : undefined}>{nTask}</b>
          </span>
        </div>
        <button type="button" className={s.saveView} onClick={noWrite}>
          <IconDeviceFloppy size={16} aria-hidden="true" />
          {t('jobList.saveView')}
        </button>
        <div className={s.sumEnd}>
          <Segmented kind="toggle" label={t('jobList.viewKind')} value="Bảng" onChange={(k) => k !== 'Bảng' && toast({ msg: t('jobList.noView') })} items={VIEWS.map((v) => ({ key: v, label: v }))} />
          <button type="button" className={s.tbCtl2} onClick={() => toast({ msg: t('jobList.noCols') })}>
            <IconLayoutColumns size={15} aria-hidden="true" />
            {t('jobList.columns')}
          </button>
        </div>
      </div>
    </>
  );

  const chipGroup = (label: string, items: string[], on: string[], set: (v: string[]) => void) => (
    <div className={s.qGroup} role="group" aria-label={label}>
      {items.map((x) => (
        <button key={x} type="button" aria-pressed={on.includes(x)} className={`${s.qBtn} ${on.includes(x) ? s.qOn : ''}`} onClick={() => set(on.includes(x) ? on.filter((y) => y !== x) : [...on, x])}>
          {x}
        </button>
      ))}
    </div>
  );
  const padToolbar = (
    <div className={s.tb}>
      <label className={`${s.tbSearch} ${s.tbSearchPad}`}>
        <select className={s.scope} aria-label={t('jobList.searchScope')} value={scope} onChange={(e) => setScope(e.target.value)}>
          {SCOPES.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
        <input aria-label={t('jobList.searchJob')} placeholder={t('jobList.searchPaste')} value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      <button type="button" className={`${s.tbBtn} ${s.tbCtl}`} onClick={() => toast({ msg: t('jobList.noRange') })}>
        <IconCalendar size={16} aria-hidden="true" />
        {t('jobList.thisMonth')} <span aria-hidden="true">▾</span>
      </button>
      {chipGroup('Phương thức', ['SEA', 'AIR'], qMode, setQMode)}
      {chipGroup('Loại', ['OB', 'IB'], qDir, setQDir)}
      <button type="button" className={`${s.tbBtn} ${s.tbCtl}`} onClick={() => toast({ msg: t('jobList.noAdv') })}>
        <IconPlus size={16} aria-hidden="true" />
        {t('jobList.filter')}
      </button>
      <div className={s.sumEnd}>
        <Segmented
          kind="toggle"
          label={t('jobList.viewKind')}
          value={view === 'card' ? 'Thẻ' : 'Bảng'}
          onChange={(k) => (k === 'Bảng' ? setViewMode('table') : k === 'Thẻ' ? setViewMode('card') : toast({ msg: t('jobList.noView') }))}
          items={VIEWS.map((v) => ({ key: v, label: v }))}
        />
      </div>
    </div>
  );

  return (
    <>
    <Page
      kind="table"
      dock={
        showDock ? (
          <JobDock
            rows={rows}
            all={all}
            now={NOW}
            tab={dockTab}
            onTab={setDockTab}
            onClose={() => setDockOn(false)}
            onSuggest={(ids) => setSelected(new Set(ids))}
            onOpenJob={(id) => void navigate({ to: `/ezy/jobs/${id}` })}
            onNoWrite={noWrite}
            onPickGroup={(g) => setTab(g === 'Active' ? 'run' : g === 'Standby' ? 'sby' : 'all')}
          />
        ) : undefined
      }
    >
      <PageHeader
        title="Job"
        actGap={8}
        tabs={layout !== 'phone' ? tabsEl : undefined}
        actions={
          asCards ? undefined : (
          <>
            {layout === 'desktop' && (
              <>
                <HeaderTool label={t('shell.analytics')} icon={IconChartBar} active={showDock && dockTab === 'an'} onClick={() => openDock('an')} />
                <HeaderTool label={t('shell.tasks')} icon={IconChecklist} n={5} active={showDock && dockTab === 'task'} onClick={() => openDock('task')} />
                <HeaderTool label={t('shell.mailList')} icon={IconMail} n={4} active={showDock && dockTab === 'mail'} onClick={() => openDock('mail')} />
                <HeaderTool label={t('shell.noti')} icon={IconBell} n={2} active={showDock && dockTab === 'noti'} onClick={() => openDock('noti')} />
                <HeaderTool label={t('shell.more')} icon={IconDots} onClick={noWrite} />
              </>
            )}
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={newJob}>
              {t('jobList.newJob')}
            </Button>
          </>
          )
        }
      />
      <div className={s.page}>
        {layout === 'desktop' && state === 'ready' && toolbar}
        {layout === 'ipad' && state === 'ready' && padToolbar}
        <div className={`${s.bar} ${layout !== 'phone' ? s.barOff : ''}`}>
          {layout === 'phone' ? phoneBar : null}
          {asCards && (
            <button type="button" className={s.quick} aria-label={t('jobList.searchJob')} aria-expanded={searchOn} onClick={() => setSearchOn((v) => !v)}>
              <IconSearch size={16} aria-hidden="true" />
            </button>
          )}
          <div className={`${s.search} ${asCards && !searchOn ? s.searchOff : ''}`}>
            <Input
              aria-label={t('jobList.searchJob')}
              placeholder={t('jobList.searchPh')}
              icon={<IconSearch size={16} aria-hidden="true" />}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>
        <SelectionBar count={selected.size} noun="job" onClear={() => setSelected(new Set())}>
          <Button size="sm" variant="secondary" onClick={noWrite}>
            {t('jobList.assign')}
          </Button>
        </SelectionBar>
        <StateView
          state={state === 'ready' && rows.length === 0 ? 'empty' : state}
          title={state === 'ready' ? 'Không có job khớp' : state === 'empty' ? 'Chưa có job' : undefined}
          desc={state === 'ready' ? 'Thử đổi tab hoặc bỏ từ khoá tìm.' : undefined}
          icon={state === 'ready' || state === 'empty' ? <IconAnchor size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('jobList.retry')}</Button> : undefined}
          skeleton={<SkeletonTable />}
        >
          {asCards || asGrid ? (
            <div ref={scroller} className={s.cardScroll}>
              {asGrid ? (
                <div role="list" aria-label={t('jobList.listLabel')}>
                  <div aria-hidden="true" style={{ height: win.padTop }} />
                  <div className={s.grid}>
                    {rows.slice(win.start * GRID_COLS, win.end * GRID_COLS).map((j) => (
                      <GridCard key={j.id} job={j} now={NOW} onOpen={open} />
                    ))}
                  </div>
                  <div aria-hidden="true" style={{ height: win.padBottom }} />
                </div>
              ) : (
                <div className={s.cardList} role="list" aria-label={t('jobList.listLabel')}>
                  <div aria-hidden="true" style={{ height: win.padTop }} />
                  {rows.slice(win.start, win.end).map((j) => (
                    <div key={j.id} role="listitem" className={s.cardWrap}>
                      <PhoneCard job={j} now={NOW} onOpen={open} onMore={noWrite} />
                    </div>
                  ))}
                  <div aria-hidden="true" style={{ height: win.padBottom }} />
                </div>
              )}
            </div>
          ) : (
          <div
            ref={scroller}
            className={s.scroller}
           
          >
            <table className={s.table} data-sel={selected.size > 0 ? '1' : undefined} aria-label={t('jobList.listLabel')} aria-rowcount={rows.length + 1}>
              <colgroup>
                <col style={{ width: 44 }} />
                {columns.map((c) => (
                  <col key={c.key} className={[c.minor ? s.minor : '', c.cmp ? s.cmp : ''].filter(Boolean).join(' ') || undefined} style={{ width: c.key === 'act' ? c.width : `${((c.width / bodyW) * 100).toFixed(2)}%` }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className={s.selCell}>
                    <input
                      type="checkbox"
                      className={s.check}
                      aria-label={t('jobList.selectAll')}
                      checked={keys.length > 0 && selected.size === keys.length}
                      onChange={() => setSelected(toggleAll(selected, keys))}
                    />
                  </th>
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      scope="col"
                      className={c.minor ? s.minor : undefined}
                      aria-sort={c.sort && sort?.key === c.sort ? (sort.dir === 'desc' ? 'descending' : 'ascending') : undefined}
                    >
                      {c.sort ? (
                        <button type="button" className={s.sortBtn} onClick={() => sortBy(c.sort as JobSortKey)}>
                          {c.header}
                          <span aria-hidden="true">{sort?.key === c.sort ? (sort.dir === 'desc' ? '↓' : '↑') : '↕'}</span>
                        </button>
                      ) : (
                        c.header
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {win.padTop > 0 && (
                  <tr aria-hidden="true" style={{ height: win.padTop }}>
                    <td colSpan={columns.length + 1} className={s.pad} />
                  </tr>
                )}
                {rows.slice(win.start, win.end).map((j, i) => {
                  const on = selected.has(j.id);
                  return (
                    <tr key={j.id} className={on ? s.rowOn : undefined} aria-rowindex={win.start + i + 2} aria-selected={on}>
                      <td className={s.selCell}>
                        <input
                          type="checkbox"
                          className={s.check}
                          aria-label={`Chọn ${j.id}`}
                          checked={on}
                          onChange={() => setSelected(toggleOne(selected, j.id))}
                        />
                      </td>
                      {columns.map((c) => (
                        <td key={c.key} className={c.minor ? s.minor : undefined}>
                          {c.cell(j)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {win.padBottom > 0 && (
                  <tr aria-hidden="true" style={{ height: win.padBottom }}>
                    <td colSpan={columns.length + 1} className={s.pad} />
                  </tr>
                )}
              </tbody>
            </table>
            <div className={s.foot}>
              <span>
                {rows.length} / {all.length} job
              </span>
              <div className={s.pager}>
                <button type="button" aria-label={t('jobList.prevPage')} onClick={noWrite}>
                  ‹
                </button>
                <button type="button" aria-label="Trang sau" onClick={noWrite}>
                  ›
                </button>
              </div>
            </div>
          </div>
          )}
        </StateView>
        {asCards && (
          <button type="button" className={s.fab} aria-label={t('jobList.createJob')} onClick={newJob}>
            <IconPlus size={26} aria-hidden="true" />
          </button>
        )}
      </div>
    </Page>
    <VesselDrawer vessel={openVessel} onClose={() => setVesselOf(null)} />
    </>
  );
}
